import React, { useContext, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { BiTestTube } from "react-icons/bi";
import { FaBacteria, FaDna } from "react-icons/fa6";
import {
  FiActivity,
  FiChevronDown,
  FiClipboard,
  FiFileText,
  FiGrid,
  FiHome,
  FiLayers,
  FiMoreHorizontal,
  FiUsers,
} from "react-icons/fi";
import { GiBullHorns, GiEarthWorm, GiPoisonBottle } from "react-icons/gi";
import { LuFlaskConical } from "react-icons/lu";
import { TbMicroscope } from "react-icons/tb";
import { AuthContext } from "../context/AuthContext";
import { SAMPLE_SECTORS } from "../constants/sampleSectors";

const sidebarGroups = [
  {
    label: "Carga",
    items: [
      { to: "/", label: "Inicio", icon: FiHome },
      { to: "/ingreso-caso", label: "Nuevo caso", icon: FiClipboard },
      { to: "/ingreso-muestra", label: "Nueva muestra", icon: FiLayers },
      { to: "/resultado-diagnostico", label: "Resultado por muestra", icon: FiFileText },
      { to: "/informe-final", label: "Diagnóstico e informe final", icon: FiFileText },
    ],
  },
  {
    label: "Seguimiento",
    items: [
      { to: "/ver-casos", label: "Ver casos", icon: FiGrid },
      { to: "/investigacion", label: "Radar IA", icon: TbMicroscope },
      { to: "/dashboard-casos", label: "Dashboard", icon: FiActivity },
    ],
  },
];

const sectorIcons = {
  HP: TbMicroscope,
  BG: LuFlaskConical,
  PA: GiEarthWorm,
  BM: FaDna,
  BAC: FaBacteria,
  PT: GiPoisonBottle,
  ITS: GiBullHorns,
  SER: BiTestTube,
  PACK_TORO: GiBullHorns,
  OTR: FiMoreHorizontal,
};

const Sidebar = ({ isOpen, onNavigate }) => {
  const { usuario } = useContext(AuthContext);
  const location = useLocation();
  const esAdmin = usuario?.role === "admin" || Boolean(usuario?.is_superuser);
  const puedeVerDashboard = Boolean(usuario?.dashboard_autorizado);
  const isBiologiaMolecularDashboard = location.pathname === "/biologia-molecular/dashboard";
  const isBiologiaMolecularForm =
    location.pathname === "/biologia-molecular" ||
    location.pathname.startsWith("/biologia-molecular/ensayo/");
  const isParasitologiaCalculator = location.pathname === "/parasitologia/dosis-respuesta";
  const isBiologiaMolecularSection = isBiologiaMolecularForm || isBiologiaMolecularDashboard;
  const isParasitologiaSection = isParasitologiaCalculator;
  const [isBiologiaMolecularMenuOpen, setIsBiologiaMolecularMenuOpen] = useState(isBiologiaMolecularSection);
  const [isParasitologiaMenuOpen, setIsParasitologiaMenuOpen] = useState(isParasitologiaSection);

  const visibleGroups = useMemo(() => {
    const filteredGroups = sidebarGroups.map((group) => ({
      ...group,
      items: group.items.filter((item) => item.to !== "/dashboard-casos" || puedeVerDashboard),
    }));

    if (!esAdmin) {
      return filteredGroups;
    }

    return [
      ...filteredGroups,
      {
        label: "Administracion",
        items: [{ to: "/usuarios", label: "Usuarios", icon: FiUsers }],
      },
    ];
  }, [esAdmin, puedeVerDashboard]);

  const biologiaMolecularItems = useMemo(() => {
    const items = [
      {
        active: isBiologiaMolecularForm,
        icon: FiClipboard,
        label: "Cargar ensayos",
        to: "/biologia-molecular",
      },
    ];

    if (puedeVerDashboard) {
      items.push({
        active: isBiologiaMolecularDashboard,
        icon: FiActivity,
        label: "Dashboard BM",
        to: "/biologia-molecular/dashboard",
      });
    }

    return items;
  }, [isBiologiaMolecularDashboard, isBiologiaMolecularForm, puedeVerDashboard]);

  const parasitologiaItems = useMemo(() => ([
    {
      active: isParasitologiaCalculator,
      icon: FiActivity,
      label: "Calculadora Dosis-Respuesta",
      to: "/parasitologia/dosis-respuesta",
    },
  ]), [isParasitologiaCalculator]);

  useEffect(() => {
    if (isBiologiaMolecularSection) {
      setIsBiologiaMolecularMenuOpen(true);
    }
  }, [isBiologiaMolecularSection]);

  useEffect(() => {
    if (isParasitologiaSection) {
      setIsParasitologiaMenuOpen(true);
    }
  }, [isParasitologiaSection]);

  return (
    <>
      <aside id="app-sidebar" className={`app-sidebar${isOpen ? " app-sidebar--open" : ""}`} aria-label="Navegacion principal">
        <div className="app-sidebar__content">
          <nav className="sidebar-nav">
            {visibleGroups.map((group) => (
              <div className="sidebar-nav__group" key={group.label}>
                <span className="sidebar-nav__label">{group.label}</span>
                <div className="sidebar-nav__items">
                  {group.items.map((item) => {
                    const Icon = item.icon;

                    return (
                      <NavLink
                        key={item.to}
                        className={({ isActive }) => `sidebar-nav__link${isActive ? " sidebar-nav__link--active" : ""}`}
                        to={item.to}
                        title={item.label}
                        aria-label={item.label}
                        onClick={onNavigate}
                      >
                        <span className="sidebar-nav__icon" aria-hidden="true">
                          <Icon />
                        </span>
                        <span className="sidebar-nav__text">{item.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            ))}
            <div className="sidebar-nav__group">
              <span className="sidebar-nav__label">Sectores</span>
              <div className="sidebar-nav__items">
                {SAMPLE_SECTORS.map((sector) => {
                  const Icon = sectorIcons[sector.value] || FiLayers;
                  const isBiologiaMolecular = sector.value === "BM";
                  const isParasitologia = sector.value === "PA";
                  const isPackToro = sector.value === "PACK_TORO";

                  if (isPackToro) {
                    return (
                      <NavLink
                        key={sector.value}
                        className={({ isActive }) => `sidebar-nav__link${isActive ? " sidebar-nav__link--active" : ""}`}
                        to="/pack-toro"
                        title={sector.label}
                        aria-label={sector.label}
                        onClick={onNavigate}
                      >
                        <span className="sidebar-nav__icon" aria-hidden="true"><Icon /></span>
                        <span className="sidebar-nav__item-copy">
                          <span className="sidebar-nav__item-label">{sector.label}</span>
                          <span className="sidebar-nav__item-meta">PACK</span>
                          <span className="sidebar-nav__badge">Activo</span>
                        </span>
                      </NavLink>
                    );
                  }
                  if (isParasitologia) {
                    return (
                      <div
                        className={`sidebar-nav__dropdown${isParasitologiaMenuOpen ? " sidebar-nav__dropdown--open" : ""}`}
                        key={sector.value}
                      >
                        <button
                          type="button"
                          className={`sidebar-nav__link sidebar-nav__link--button${isParasitologiaSection ? " sidebar-nav__link--active" : ""}`}
                          aria-expanded={isParasitologiaMenuOpen}
                          title={sector.label}
                          aria-label={sector.label}
                          onClick={() => setIsParasitologiaMenuOpen((open) => !open)}
                        >
                          <span className="sidebar-nav__icon" aria-hidden="true">
                            <Icon />
                          </span>
                          <span className="sidebar-nav__item-copy">
                            <span className="sidebar-nav__item-label">{sector.label}</span>
                            <span className="sidebar-nav__item-meta">{sector.value}</span>
                            <span className="sidebar-nav__badge">Activo</span>
                          </span>
                          <span className="sidebar-nav__dropdown-chevron" aria-hidden="true">
                            <FiChevronDown />
                          </span>
                        </button>

                        {isParasitologiaMenuOpen ? (
                          <div className="sidebar-nav__submenu">
                            {parasitologiaItems.map((item) => {
                              const ItemIcon = item.icon;

                              return (
                                <NavLink
                                  key={item.to}
                                  className={`sidebar-nav__sublink${item.active ? " sidebar-nav__sublink--active" : ""}`}
                                  to={item.to}
                                  title={item.label}
                                  aria-label={item.label}
                                  onClick={onNavigate}
                                >
                                  <span className="sidebar-nav__subicon" aria-hidden="true">
                                    <ItemIcon />
                                  </span>
                                  <span className="sidebar-nav__subtext">{item.label}</span>
                                </NavLink>
                              );
                            })}
                          </div>
                        ) : null}
                      </div>
                    );
                  }

                  if (isBiologiaMolecular) {
                    return (
                      <div
                        className={`sidebar-nav__dropdown${isBiologiaMolecularMenuOpen ? " sidebar-nav__dropdown--open" : ""}`}
                        key={sector.value}
                      >
                        <button
                          type="button"
                          className={`sidebar-nav__link sidebar-nav__link--button${isBiologiaMolecularSection ? " sidebar-nav__link--active" : ""}`}
                          aria-expanded={isBiologiaMolecularMenuOpen}
                          title={sector.label}
                          aria-label={sector.label}
                          onClick={() => setIsBiologiaMolecularMenuOpen((open) => !open)}
                        >
                          <span className="sidebar-nav__icon" aria-hidden="true">
                            <Icon />
                          </span>
                          <span className="sidebar-nav__item-copy">
                            <span className="sidebar-nav__item-label">{sector.label}</span>
                            <span className="sidebar-nav__item-meta">{sector.value}</span>
                            <span className="sidebar-nav__badge">Activo</span>
                          </span>
                          <span className="sidebar-nav__dropdown-chevron" aria-hidden="true">
                            <FiChevronDown />
                          </span>
                        </button>

                        {isBiologiaMolecularMenuOpen ? (
                          <div className="sidebar-nav__submenu">
                            {biologiaMolecularItems.map((item) => {
                              const ItemIcon = item.icon;

                              return (
                                <NavLink
                                  key={item.to}
                                  className={`sidebar-nav__sublink${item.active ? " sidebar-nav__sublink--active" : ""}`}
                                  to={item.to}
                                  title={item.label}
                                  aria-label={item.label}
                                  onClick={onNavigate}
                                >
                                  <span className="sidebar-nav__subicon" aria-hidden="true">
                                    <ItemIcon />
                                  </span>
                                  <span className="sidebar-nav__subtext">{item.label}</span>
                                </NavLink>
                              );
                            })}
                          </div>
                        ) : null}
                      </div>
                    );
                  }

                  return (
                    <div
                      className="sidebar-nav__link sidebar-nav__link--disabled"
                      key={sector.value}
                      aria-disabled="true"
                      aria-label={`${sector.label} — Próximamente`}
                      title={`${sector.label} — Próximamente`}
                    >
                      <span className="sidebar-nav__icon" aria-hidden="true">
                        <Icon />
                      </span>
                      <span className="sidebar-nav__item-copy">
                        <span className="sidebar-nav__item-label">{sector.label}</span>
                        <span className="sidebar-nav__item-meta">{sector.value}</span>
                        <span className="sidebar-nav__badge">{"Pr\u00f3ximamente"}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </nav>
        </div>
      </aside>

      {isOpen && <button type="button" className="app-sidebar__backdrop" aria-label="Cerrar menu lateral" onClick={onNavigate} />}
    </>
  );
};

export default Sidebar;
